/**
 * Project settings → Priority. Pick a preset (Balanced, Compliance-first,
 * Task-first) or edit the points, preview how the Fix next top 10 moves,
 * and save a new weights version (owner). Saving re-scores the project.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { PriorityWeights } from "@iamu/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useProjectRole } from "@/lib/use-project-role";
import { features } from "@/lib/features";

type Preset = "balanced" | "compliance_first" | "task_first" | "custom";

const ALL_FIELDS: Array<[keyof PriorityWeights, string, string]> = [
  ["severity", "critical", "Critical severity"],
  ["severity", "major", "Major severity"],
  ["severity", "minor", "Minor severity"],
  ["severity", "nit", "Nit"],
  ["taskImpact", "blocks", "Blocks the task"],
  ["taskImpact", "impairs", "Impairs the task"],
  ["taskImpact", "slows", "Slows the task"],
  ["humanTest", "no", "Judge verdict: no"],
  ["humanTest", "partially", "Judge verdict: partially"],
  ["regulatoryWeight", "hard_requirement", "Hard requirement"],
  ["regulatoryWeight", "process_model", "Process model"],
  ["regulatoryWeight", "best_practice", "Best practice"],
  ["reach", "twoPersonas", "Two personas"],
  ["reach", "threePlusPersonas", "Three or more personas"],
  ["reach", "twoPlusScenarios", "Two or more scenarios"],
  ["recurrence", "seenTwice", "Seen twice"],
  ["recurrence", "seenThreePlus", "Seen three or more times"],
  ["recurrence", "persistedAfterFix", "Persisted after a fix"],
];

const FIELDS = ALL_FIELDS.filter(
  ([group]) => features.knowledgeSources || group !== "regulatoryWeight",
);

export function PriorityWeightsSection({ projectId }: { projectId: string }) {
  const { isOwner } = useProjectRole(projectId);
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.priorityWeights.get.useQuery({ projectId });
  const [preset, setPreset] = useState<Preset>("balanced");
  const [weights, setWeights] = useState<PriorityWeights | null>(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!data) return;
    setPreset(data.preset as Preset);
    setWeights(data.weights);
  }, [data]);

  const input = {
    projectId,
    preset,
    ...(preset === "custom" && weights ? { weights } : {}),
  };
  const preview = trpc.priorityWeights.preview.useQuery(input, {
    enabled: previewing && !!weights,
  });
  const save = trpc.priorityWeights.set.useMutation({
    onSuccess: (res) => {
      toast.success(
        res.rescored === "done"
          ? `Weights v${res.version} saved; priorities updated`
          : `Weights v${res.version} saved; re-scoring in the background`,
      );
      utils.priorityWeights.get.invalidate({ projectId });
      utils.finding.queue.invalidate({ projectId });
      setPreviewing(false);
    },
    onError: (e) => toastTrpcError(e, "Failed to save priority weights"),
  });

  if (isLoading || !data || !weights) return <Skeleton className="h-40" />;

  function choosePreset(p: Preset) {
    setPreset(p);
    setPreviewing(false);
    const found = data?.presets.find((x) => x.key === p);
    if (found) setWeights(found.weights as PriorityWeights);
  }

  function setPoint(group: keyof PriorityWeights, key: string, value: string) {
    const n = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
    setPreset("custom");
    setPreviewing(false);
    setWeights((w) =>
      w
        ? {
            ...w,
            [group]: { ...(w[group] as Record<string, number>), [key]: n },
          }
        : w,
    );
  }

  return (
    <Card>
      <CardContent className="space-y-6 p-6">
        <p className="text-sm text-muted-foreground">
          Current version: v{data.version}.
          {isOwner ? "" : " Only project owners can change the weights."}
        </p>
        <div className="max-w-sm space-y-2">
          <Label htmlFor="priority-preset">Preset</Label>
          <Select
            value={preset}
            onValueChange={(v) => choosePreset(v as Preset)}
            disabled={!isOwner}
          >
            <SelectTrigger id="priority-preset">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {data.presets
                .filter(
                  (p) =>
                    features.knowledgeSources || p.key !== "compliance_first",
                )
                .map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                  </SelectItem>
                ))}
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <fieldset className="space-y-3">
          <legend className="text-md font-semibold">Points per factor</legend>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FIELDS.map(([group, key, label]) => {
              const id = `w-${group}-${key}`;
              return (
                <div
                  key={id}
                  className="flex items-center justify-between gap-3"
                >
                  <Label htmlFor={id} className="font-normal">
                    {label}
                  </Label>
                  <Input
                    id={id}
                    type="number"
                    min={0}
                    max={100}
                    className="h-9 w-20 tabular-nums"
                    disabled={!isOwner}
                    value={(weights[group] as Record<string, number>)[key]}
                    onChange={(e) => setPoint(group, key, e.target.value)}
                  />
                </div>
              );
            })}
          </div>
        </fieldset>

        {isOwner && (
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setPreviewing(true)}>
              Preview the top 10
            </Button>
            <Button
              onClick={() => save.mutate(input)}
              disabled={save.isPending}
            >
              Save weights
            </Button>
          </div>
        )}

        {previewing &&
          (preview.isLoading || !preview.data ? (
            <Skeleton className="h-40" />
          ) : (
            <Table>
              <TableCaption>
                Fix next, current weights vs the proposed ones. Nothing is saved
                until you save.
              </TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">#</TableHead>
                  <TableHead scope="col">Proposed</TableHead>
                  <TableHead scope="col" className="text-right">
                    Score
                  </TableHead>
                  <TableHead scope="col">Movement</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.data.proposed.map((r, i) => (
                  <TableRow key={r.id}>
                    <TableCell className="tabular-nums">{i + 1}</TableCell>
                    <TableCell className="max-w-[40ch] truncate">
                      {r.title ?? "Untitled"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.band} · {r.score}
                    </TableCell>
                    <TableCell className="text-sm">
                      {movement(i + 1, r.otherRank)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ))}
      </CardContent>
    </Card>
  );
}

function movement(now: number, before: number | null): string {
  if (before === null) return "New in the top 10";
  if (before === now) return "No change";
  return before > now ? `Up ${before - now}` : `Down ${now - before}`;
}
